import React, { useEffect, useState } from 'react';
import { EmojiSad } from 'iconsax-react';
import { useDispatch, useSelector } from 'react-redux';
import { logout, setToken } from 'store/actions/user';
import { useToast } from 'hooks/useToast';
import { RootState } from 'types/store';

export enum Methods {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE'
}

export type Config = {
  endpoint: string;
  method?: Methods;
};

const API_URL = process.env.REACT_APP_URL;

function objToQueryString(obj: any) {
  const keyValuePairs = [];

  for (const key in obj) {
    keyValuePairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`);
  }

  return keyValuePairs.join('&');
}

export default function useApi() {
  const { token, refreshToken } = useSelector((state: RootState) => state.user);
  // const { selectedCompanyId } = useSelector((state: RootState) => state.general);
  const dispatch = useDispatch();
  const notify = useToast();
  const [response, setResponse] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  async function fetchData(config: Config, data?: any, successMessage: boolean = false, errorMessageOverride?: string) {
    const headers: Headers = new Headers();
    const method = config.method || Methods.GET;
    let url = `${API_URL}${config.endpoint}`;

    setIsLoading(true);

    if (!(data instanceof FormData)) {
      headers.append('Content-Type', 'application/json');
    }
    !!token && headers.append('Authorization', `Bearer ${token}`);
    !!refreshToken && headers.append('refresh-token', refreshToken);
    // !!selectedCompanyId && headers.append('x-company-id', selectedCompanyId);

    if (data && method === Methods.GET) {
      url += `?${objToQueryString(data)}`;
    }

    try {
      const r = await fetch(url, {
        method,
        headers,
        body: data && method !== Methods.GET ? (data instanceof FormData ? data : JSON.stringify(data)) : undefined
      });

      if (!r.ok) {
        try {
          const parsed = await r.json();
          const errorMessage = errorMessageOverride || parsed?.message || r.statusText;
          notify(errorMessage, <EmojiSad />);

          if (r.status === 401) {
            dispatch(logout());
          }
        } catch {
          const msg = errorMessageOverride || r.statusText;
          notify(msg, <EmojiSad />);
        }
      } else {
        const response = await r.json();
        setResponse(response);
        successMessage && notify(response.message);
        return response;
      }
    } catch {
      // Network error / fetch threw (e.g., timeout via network infra)
      const msg = errorMessageOverride || 'Request failed. Please try again.';
      notify(msg, <EmojiSad />);
    } finally {
      setIsLoading(false);
      setLoaded(true);
    }
  }

  const uploadFileToS3 = async (file: Blob, fileType: string, url: string) => {
    setIsLoading(true);

    const contentTypes: { [key: string]: string } = {
      pdf: 'application/pdf',
      jpeg: 'image/jpeg',
      png: 'image/png'
    };

    const headers = new Headers();
    headers.append('Content-Type', contentTypes[fileType]);

    try {
      const response = await fetch(url, {
        method: Methods.PUT,
        headers,
        body: file
      });

      if (!response.ok) {
        console.error('Fetch error');
      }
    } catch (error) {
      console.error('Fetch error', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (response && response.token) {
      dispatch(setToken(response.token));
    }
  }, [response]);

  return { response, isLoading, fetchData, uploadFileToS3, loaded };
}
